(() => {
  const initQuickView = () => {
    if (window.carsonProductQuickViewInitialized) return;
    window.carsonProductQuickViewInitialized = true;

    const openClass = 'carson-best-sellers-modal-open';
    let activeModal = null;
    let activeModalOrigin = null;
    let previousFocus = null;

    const getFocusable = (modal) =>
      Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

    const openModal = (modal) => {
      if (!modal) return;

      previousFocus = document.activeElement;
      activeModal = modal;
      activeModalOrigin = {
        parent: modal.parentNode,
        nextSibling: modal.nextSibling,
      };

      document.body.appendChild(modal);
      modal.hidden = false;
      document.body.classList.add(openClass);

      const dialog = modal.querySelector('[role="dialog"]');
      const focusable = getFocusable(modal);
      (focusable[0] || dialog)?.focus();
    };

    const closeModal = () => {
      if (!activeModal) return;

      const modal = activeModal;
      const origin = activeModalOrigin;

      modal.hidden = true;
      activeModal = null;
      activeModalOrigin = null;
      document.body.classList.remove(openClass);

      if (origin?.parent?.isConnected) {
        const nextSibling = origin.nextSibling?.parentNode === origin.parent ? origin.nextSibling : null;
        origin.parent.insertBefore(modal, nextSibling);
      } else {
        modal.remove();
      }

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-carson-quick-view-open]');
      if (opener) {
        event.preventDefault();
        openModal(document.getElementById(opener.dataset.carsonQuickViewOpen));
        return;
      }

      const closeTrigger = event.target.closest('[data-carson-quick-view-close]');
      const dialog = activeModal?.querySelector('.carson-best-sellers-modal__dialog');
      const clickedOutsideDialog =
        activeModal && dialog && activeModal.contains(event.target) && !dialog.contains(event.target);

      if (closeTrigger || clickedOutsideDialog) {
        event.preventDefault();
        closeModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!activeModal) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(activeModal);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initSliderController = () => {
    if (window.carsonBestSellersSliderInitialized) {
      window.carsonBestSellersRefreshAll?.();
      return;
    }

    window.carsonBestSellersSliderInitialized = true;

    const sections = new WeakMap();
    const toBoolean = (value) => value === 'true';
    const toNumber = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const getVisibleSlides = () => {
      const width = window.innerWidth;

      if (width >= 1440) return 4;
      if (width >= 990) return 3;
      if (width >= 620) return 2;
      return 1;
    };

    const getSlideCount = (section, slides) => {
      const itemCount = toNumber(section?.dataset.itemCount, 0);
      return itemCount || slides?.length || 0;
    };

    const getSliderState = (section, slides) => {
      const slideCount = getSlideCount(section, slides);
      const visibleSlides = getVisibleSlides();
      const hasOverflow = slideCount > visibleSlides;

      return {
        slideCount,
        visibleSlides,
        hasOverflow,
        shouldLoop: toBoolean(section?.dataset.loop) && slideCount > visibleSlides + 1,
      };
    };

    const syncMediaHeight = (section) => {
      const media = section?.querySelector('.carson-best-sellers-card__media');
      if (!media) return;

      section.style.setProperty('--carson-best-sellers-media-height', `${Math.round(media.getBoundingClientRect().height)}px`);
    };

    const updateArrowState = (section, slides, sliderState = getSliderState(section, slides)) => {
      if (!section) return;

      const shouldShowArrows = sliderState.hasOverflow;
      const controls = section.querySelector('.carson-best-sellers__controls');

      section.classList.toggle('is-arrow-active', shouldShowArrows);

      if (controls) {
        controls.hidden = !shouldShowArrows;
      }
    };

    const destroySection = (section) => {
      const sectionState = sections.get(section);
      const swiper = sectionState?.swiper || sectionState;

      if (swiper) {
        swiper.destroy(true, true);
        sections.delete(section);
      }

      section?.classList.remove('is-swiper-active');
      section?.classList.remove('is-arrow-active');
    };

    const initSection = (section) => {
      if (!section) return;

      const existingSectionState = sections.get(section);
      if (existingSectionState) {
        const slides = section.querySelectorAll('.swiper-slide');
        const sliderState = getSliderState(section, slides);
        syncMediaHeight(section);
        updateArrowState(section, slides, sliderState);

        if (
          existingSectionState.slideCount !== sliderState.slideCount ||
          existingSectionState.visibleSlides !== sliderState.visibleSlides ||
          existingSectionState.hasOverflow !== sliderState.hasOverflow ||
          existingSectionState.shouldLoop !== sliderState.shouldLoop
        ) {
          destroySection(section);
          initSection(section);
          return;
        }

        existingSectionState.swiper.update();
        return;
      }

      const swiperElement = section.querySelector('[data-carson-best-sellers-swiper]');
      const slides = swiperElement?.querySelectorAll('.swiper-slide') || [];
      const sliderState = getSliderState(section, slides);

      syncMediaHeight(section);
      updateArrowState(section, slides, sliderState);

      if (!swiperElement || slides.length <= 1) {
        destroySection(section);
        return;
      }

      const swiperReady = window.CarsonSwiperLoader?.readyFor(section);
      if (!swiperReady) return;

      swiperReady.then(() => {
        if (sections.has(section)) return;

        const nextSliderState = getSliderState(section, slides);
        const delay = toNumber(section.dataset.delay, 3200);
        const speed = toNumber(section.dataset.speed, 700);
        const prevButton = section.querySelector('[data-carson-best-sellers-prev]');
        const nextButton = section.querySelector('[data-carson-best-sellers-next]');

        const config = {
          slidesPerView: nextSliderState.visibleSlides,
          slidesPerGroup: 1,
          spaceBetween: 16,
          speed,
          a11y: {
            slideRole: 'listitem',
          },
          loop: nextSliderState.shouldLoop,
          rewind: !nextSliderState.shouldLoop && nextSliderState.hasOverflow,
          autoplay: nextSliderState.hasOverflow && toBoolean(section.dataset.autoplay)
            ? {
                delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false,
          allowTouchMove: nextSliderState.hasOverflow,
          grabCursor: nextSliderState.hasOverflow,
          observer: true,
          observeParents: true,
          resizeObserver: true,
          roundLengths: true,
          watchOverflow: true,
          breakpoints: {
            620: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            990: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
            1440: {
              slidesPerView: 4,
              spaceBetween: 28,
            },
          },
        };

        if (prevButton && nextButton) {
          config.navigation = {
            prevEl: prevButton,
            nextEl: nextButton,
          };
        }

        section.classList.add('is-swiper-active');
        syncMediaHeight(section);
        updateArrowState(section, slides, nextSliderState);

        const swiper = new window.Swiper(swiperElement, config);
        sections.set(section, {
          swiper,
          slideCount: nextSliderState.slideCount,
          visibleSlides: nextSliderState.visibleSlides,
          hasOverflow: nextSliderState.hasOverflow,
          shouldLoop: nextSliderState.shouldLoop,
        });
        swiper.update();

        window.requestAnimationFrame(() => {
          syncMediaHeight(section);
          updateArrowState(section, slides);
          swiper.update();
        });
      }).catch(() => {});
    };

    const refreshSection = (section) => {
      if (!section) return;
      initSection(section);
    };

    const refreshAll = () => {
      document.querySelectorAll('[data-carson-best-sellers-section]').forEach(refreshSection);
    };

    window.carsonBestSellersRefreshAll = refreshAll;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', refreshAll);
    } else {
      refreshAll();
    }

    window.addEventListener('resize', refreshAll);

    document.addEventListener('shopify:section:load', (event) => {
      refreshSection(event.target.querySelector('[data-carson-best-sellers-section]'));
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroySection(event.target.querySelector('[data-carson-best-sellers-section]'));
    });
  };

  initQuickView();
  initSliderController();
})();
